import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, CloudArrowDown, DeviceMobile, ShieldCheck } from '@phosphor-icons/react'
import { api, ApiError, useNetworkStatus } from '@/api'
import { useAuth } from '@/auth/context'
import { useFormat, useI18n } from '@/i18n'
import { DemoDeviceError, downloadCaches, refreshRegistration, useDeviceInfo } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  KeyValue,
  Logo,
  StatusPill,
  useToast,
} from '@/ui'
import { LanguageToggle } from '../components/LanguageToggle'
import { listWords } from '../listWords'

/**
 * First run on a phone (F4.1-d/-f): one online sign-in, then "Set up this phone" registers it to the
 * signer's village (the server does it; the office step is off) and downloads the roster. An office
 * sign-in, or someone on several villages, picks the village first.
 */
export function DeviceSetupPage() {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const { status } = useAuth()
  const { online } = useNetworkStatus()
  const info = useDeviceInfo()
  const [busy, setBusy] = useState<'check' | 'roster' | 'seed' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The server could not tell which village (office sign-in, or several villages): pick one.
  const [villages, setVillages] = useState<Array<{ id: string; name: string }> | null>(null)
  const [villageId, setVillageId] = useState('')

  const reg = info.registration
  const words = listWords(reg?.team.kind)
  const revoked = reg?.status === 'REVOKED'
  const ready = Boolean(reg && !revoked && info.freshness !== 'missing')

  const run = async (kind: 'check' | 'roster' | 'seed', fn: () => Promise<void>) => {
    setBusy(kind)
    setError(null)
    try {
      await fn()
    } catch (e) {
      if (e instanceof DemoDeviceError) setError(t('device.demoOffline'))
      else if (e instanceof ApiError && /NO_VILLAGE|SEVERAL_VILLAGES/.test(e.message)) {
        setError(t('device.pickVillageHint'))
        try {
          const page = await api.paginated<Record<string, unknown>>('/groups', {
            query: { page: 1, limit: 200 },
          })
          setVillages(
            page.data
              .filter((g) => !g.type || String(g.type).toUpperCase() === 'VILLAGE')
              .map((g) => ({ id: String(g.id), name: String(g.name) }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          )
        } catch {
          setVillages([])
        }
      } else if (e instanceof ApiError && e.kind === 'not_found')
        setError(e.message || t('device.notRegistered'))
      else if (e instanceof ApiError && (e.kind === 'network' || e.kind === 'timeout'))
        setError(t('auth.needsNetwork'))
      else setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-screen-sm flex-col bg-paper px-4 pb-10">
      <div className="pt-safe flex items-center justify-between py-4">
        <Logo />
        <LanguageToggle />
      </div>

      <h1 className="text-[26px] leading-tight">
        <BilingualLabel
          k="device.title"
          layout="stacked"
          secondaryClassName="text-[15px] font-sans"
        />
      </h1>
      <p className="mt-2 text-[14px] text-muted">{t('device.body')}</p>

      <div className="mt-5 flex flex-col gap-4">
        {revoked && <Banner tone="error">{t('device.revoked')}</Banner>}
        {error && <Banner tone="error">{error}</Banner>}
        {status !== 'authenticated' && (
          <Banner
            tone="info"
            action={
              <Link
                to="/login?next=/m/setup"
                className="font-mono text-[12px] font-bold uppercase tracking-label text-info underline"
              >
                {t('auth.signIn')}
              </Link>
            }
          >
            {t('auth.signInHint')}
          </Banner>
        )}

        <Card>
          <CardHeader
            eyebrow={<BilingualLabel k="device.uid" />}
            actions={<DeviceMobile size={22} className="text-amber-dark" aria-hidden="true" />}
          />
          <CardBody>
            <div className="break-all rounded-chip bg-paper-tint px-3 py-3 text-center font-mono text-[17px] font-medium tracking-wide text-ink">
              {info.deviceUid ?? '…'}
            </div>
            <KeyValue
              className="mt-3"
              items={[
                {
                  label: <BilingualLabel k="device.status" />,
                  value: reg ? (
                    <StatusPill
                      status={reg.status}
                      label={revoked ? undefined : t('device.registered')}
                    />
                  ) : (
                    t('device.notRegistered')
                  ),
                },
                ...(reg
                  ? [
                      { label: <BilingualLabel k="device.team" />, value: reg.team.name },
                      {
                        label: <BilingualLabel k="device.prefix" />,
                        value: reg.code_prefix,
                        mono: true,
                      },
                    ]
                  : []),
                {
                  label: <BilingualLabel k={words.label} />,
                  value: info.rosterDownloadedAt
                    ? t(words.age, { when: fmt.relative(info.rosterDownloadedAt) })
                    : t(words.never),
                },
              ]}
            />
          </CardBody>
        </Card>

        {villages && !reg && (
          <label className="flex flex-col gap-1.5">
            <span className="text-[15px] font-semibold">{t('device.pickVillage')}</span>
            <select
              value={villageId}
              onChange={(e) => setVillageId(e.target.value)}
              className="h-12 rounded-chip border-[1.5px] border-rule bg-surface px-3 text-[17px]"
            >
              <option value="">{t('device.pickVillage')}</option>
              {villages.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex flex-col gap-2">
          <Button
            variant="secondary"
            size="touch"
            fullWidth
            leadingIcon={<ShieldCheck aria-hidden="true" />}
            loading={busy === 'check'}
            disabled={!online || status !== 'authenticated' || busy !== null}
            // Office registration is switched off for the pilot: signing in and tapping this sets the
            // phone up for the signed-in person's village (the server registers it) and downloads
            // the farmer list and team PINs in one go. After that only the PIN is needed.
            // onClick={() => void run('check', async () => void (await refreshRegistration()))}
            onClick={() =>
              void run('check', async () => {
                await refreshRegistration(undefined, villageId || undefined)
                await downloadCaches()
                setVillages(null)
                toast.success(t(words.updated))
              })
            }
          >
            {t('device.check')}
          </Button>
          <Button
            size="touch"
            fullWidth
            leadingIcon={<CloudArrowDown aria-hidden="true" />}
            loading={busy === 'roster'}
            disabled={!online || status !== 'authenticated' || !reg || revoked || busy !== null}
            onClick={() =>
              void run('roster', async () => {
                await downloadCaches()
                toast.success(t(words.updated))
              })
            }
          >
            {t(words.update)}
          </Button>
          {import.meta.env.DEV && (
            <Button
              variant="ghost"
              size="touch"
              fullWidth
              loading={busy === 'seed'}
              disabled={busy !== null}
              onClick={() =>
                void run('seed', async () => {
                  const { seedDevDevice } = await import('@/offline/devSeed')
                  await seedDevDevice('VILLAGE')
                })
              }
            >
              {t('device.devSeed')}
            </Button>
          )}
          {import.meta.env.DEV && (
            <Button
              variant="ghost"
              size="touch"
              fullWidth
              loading={busy === 'seed'}
              disabled={busy !== null}
              onClick={() =>
                void run('seed', async () => {
                  const { seedDevDevice } = await import('@/offline/devSeed')
                  await seedDevDevice('SITE')
                })
              }
            >
              {t('store.devSeed')}
            </Button>
          )}
          {import.meta.env.DEV &&
            (['PROCESSING', 'PACKING'] as const).map((k) => (
              <Button
                key={k}
                variant="ghost"
                size="touch"
                fullWidth
                loading={busy === 'seed'}
                disabled={busy !== null}
                onClick={() =>
                  void run('seed', async () => {
                    const { seedDevDevice } = await import('@/offline/devSeed')
                    await seedDevDevice(k)
                  })
                }
              >
                {t(k === 'PROCESSING' ? 'process.devSeed' : 'pack.devSeed')}
              </Button>
            ))}
        </div>

        {ready && (
          <Link
            to="/m/unlock"
            className="mt-2 inline-flex min-h-touch-lg items-center justify-center gap-2 rounded-chip border border-ink bg-ink px-6 font-mono text-[14px] font-bold uppercase tracking-label text-paper"
          >
            {t('common.continue')}
            <ArrowRight aria-hidden="true" />
          </Link>
        )}
      </div>
    </main>
  )
}
